import { useState, useEffect, useRef, useCallback } from 'react';
import { isMobile } from 'react-device-detect';

const KEYBOARD_HIDE_THRESHOLD = 150;
const BLUR_DELAY = 350; // blur 이벤트 발생 후 상태 변경까지의 지연 시간 (ms)

export const useVirtualKeyboardVisible = () => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const isInputFocusedRef = useRef(false);
  const lastHeightRef = useRef(typeof window !== 'undefined' ? window.innerHeight : 0);
  // setTimeout의 타이머 ID를 저장하기 위한 ref
  const blurTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleFocus = useCallback(() => {
    if (!isMobile) return;

    if (blurTimerRef.current) {
      // 만약 이전에 설정된 blur 타이머가 있다면 즉시 취소
      clearTimeout(blurTimerRef.current);
    }

    isInputFocusedRef.current = true;
    setIsKeyboardVisible(true);
  }, []);

  const handleBlur = useCallback(() => {
    if (!isMobile) return;

    // setTimeout을 사용해 상태 변경을 지연시킴
    blurTimerRef.current = setTimeout(() => {
      isInputFocusedRef.current = false;
      setIsKeyboardVisible(false);
    }, BLUR_DELAY);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const handleResize = () => {
      if (!isInputFocusedRef.current) {
        return;
      }
      const currentHeight = window.innerHeight;
      const heightDifference = currentHeight - lastHeightRef.current;
      if (heightDifference > KEYBOARD_HIDE_THRESHOLD) {
        setIsKeyboardVisible(false);
        isInputFocusedRef.current = false;
      }
      lastHeightRef.current = currentHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  return { isKeyboardVisible, handleFocus, handleBlur };
};
